import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function RegisterStudent() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", registration_number: "", roll_number: "",
    institution: "", password: "", confirm_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/student", form);
      login(data.token, data.user);
      navigate("/pending");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Student enrollment"
      subtitle="Register to take examinations. A valid registration number is required."
      side="Your assessments, all in one place."
      image="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2000"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="student-register-form">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2" data-testid="register-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Full Name</Label>
          <Input value={form.name} onChange={set("name")} required data-testid="reg-name" />
        </div>
        <div className="space-y-1.5">
          <Label>Email Address</Label>
          <Input type="email" value={form.email} onChange={set("email")} required data-testid="reg-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Registration No. *</Label>
            <Input value={form.registration_number} onChange={set("registration_number")} required data-testid="reg-regno" placeholder="REG-1001" />
          </div>
          <div className="space-y-1.5">
            <Label>Roll Number</Label>
            <Input value={form.roll_number} onChange={set("roll_number")} required data-testid="reg-rollno" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Institution</Label>
          <Input value={form.institution} onChange={set("institution")} required data-testid="reg-institution" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required data-testid="reg-password" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm</Label>
            <Input type="password" value={form.confirm_password} onChange={set("confirm_password")} required data-testid="reg-confirm" />
          </div>
        </div>
        <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="register-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        Already enrolled?{" "}
        <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
      </div>
    </AuthShell>
  );
}
